import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  EndNotification,
  ItemMeta,
  ItemMetaContent,
  NotificationItem,
  NotificationDestination,
  NotificationListWrapper,
  StyledDropdown,
  WrapperComponent
} from './NotificationPopup.styled'
import { Icon } from '..'
import { useFetch } from '@core/hooks'
import { APIS } from '@constants/apis'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Skeleton } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { getUser } from '@core/utils'
import { ROLE_TYPE } from '@constants/role'
import { NOTIFICATION_TYPE } from '@constants/notification'
import { ELEAVE_EMPLOYEE_TAB_STATE } from '@constants/tabState'
import { flatMap, isEqual, map, uniqWith } from 'lodash'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

const NotificationPopup = ({ trigger = ['click'], ...props }, ref) => {
  const { t } = useTranslation()
  const [notiList, setNotiList] = useState([])
  const [open, setOpen] = useState(false)
  const history = useHistory()
  const userData = getUser()
  const params = useParams()

  const { execute: _getNotification, pending } = useFetch(
    APIS.GENERALS.GET_NOTIFICATION
  )

  const { execute: _updateNotification } = useFetch(
    APIS.GENERALS.UPDATE_NOTIFICATION
  )

  const paginationRef = useRef({
    currentPage: 1,
    perPage: 10,
    total: 0,
    unreadCount: 0
  })

  const getList = useCallback(
    ({ page, update } = { page: 1, update: false }) => {
      _getNotification(
        {
          payload: {
            page: page || paginationRef.current.currentPage,
            limit: 10
          }
        },
        {
          onSuccess: ({ data: { docs, ...rest } }) => {
            paginationRef.current = { ...rest }

            const newList = { list: [...docs], page: page }

            if (update) {
              return setNotiList((preList) =>
                map(preList, (obj) => {
                  if (obj.page === newList.page) {
                    return { ...obj, list: newList.list }
                  }
                  return obj
                })
              )
            }

            return setNotiList((preList) =>
              preList.concat({ page: page, list: [...docs] })
            )
          }
        }
      )
    },
    [_getNotification]
  )

  const updateNotification = useCallback(
    ({ id, type, page, identifier }) => {
      _updateNotification(
        { url: id, payload: { isRead: true } },
        {
          onFinally: () => {
            getList({ page, update: true })
          }
        }
      )
      if (
        userData?._pis?.roleType === ROLE_TYPE.EMPLOYEE &&
        [
          NOTIFICATION_TYPE.LEAVE_APPROVAL,
          NOTIFICATION_TYPE.LEAVE_SUCCESS
        ].includes(type)
      ) {
        history.replace(`/${params.companyGroup}/${params.companyId}/e-leave`, {
          tabState:
            type === NOTIFICATION_TYPE.LEAVE_APPROVAL
              ? ELEAVE_EMPLOYEE_TAB_STATE.APPROVE
              : ELEAVE_EMPLOYEE_TAB_STATE.HISTORY,
          ...(type === NOTIFICATION_TYPE.LEAVE_APPROVAL &&
            Number.isSafeInteger(Number(identifier)) &&
            Number(identifier) > 0 && {
              notificationApprovalTxId: Number(identifier)
            }),
          ...(type === NOTIFICATION_TYPE.LEAVE_SUCCESS &&
            Number.isSafeInteger(Number(identifier)) &&
            Number(identifier) > 0 && {
              notificationLeaveTxId: Number(identifier)
            })
        })
        setOpen(false)
      }
    },
    [
      _updateNotification,
      getList,
      history,
      params.companyGroup,
      params.companyId,
      userData?._pis?.roleType
    ]
  )

  useEffect(() => {
    getList()
  }, [getList])

  const handleScroll = useCallback(() => {
    const nextPage = paginationRef.current.currentPage + 1
    getList({ page: nextPage })
  }, [getList])

  const handleClickNoti = useCallback(
    ({ id, type, page, identifier }) => {
      updateNotification({ id, type, page, identifier })
    },
    [updateNotification]
  )

  const updateNotiPromise = useCallback(
    (id) =>
      new Promise((resolve) => {
        _updateNotification(
          { url: `${id}`, payload: { isRead: true } },
          { onFinally: resolve }
        )
      }),
    [_updateNotification]
  )

  const handleMarkAll = useCallback(async () => {
    const allLoaded = flatMap(map(notiList, 'list'))
    const unreadIds = uniqWith(allLoaded, isEqual)
      .filter((x) => !x.isRead)
      .map((x) => x.id)

    if (!unreadIds.length) return

    setNotiList((prev) =>
      map(prev, (pageObj) => ({
        ...pageObj,
        list: map(pageObj.list, (it) =>
          unreadIds.includes(it.id) ? { ...it, isRead: true } : it
        )
      }))
    )

    await Promise.all(unreadIds.map(updateNotiPromise))

    setNotiList([])
    paginationRef.current.currentPage = 1
    getList({ page: 1 })
  }, [notiList, getList, updateNotiPromise])

  const renderNotification = React.useCallback(
    ({ list, page }) => {
      return map(uniqWith(list, isEqual), (item, id) => (
        <NotificationItem
          key={id}
          onClick={() => {
            handleClickNoti({
              id: item.id,
              type: item.type,
              page,
              identifier: item.identifier
            })
          }}
        >
          <ItemMeta
            description={
              <ItemMetaContent>
                {[
                  NOTIFICATION_TYPE.LEAVE_APPROVAL,
                  NOTIFICATION_TYPE.LEAVE_SUCCESS
                ].includes(item.type) && (
                  <NotificationDestination
                    data-kind={
                      item.type === NOTIFICATION_TYPE.LEAVE_APPROVAL
                        ? 'approval'
                        : 'result'
                    }
                  >
                    {t(
                      item.type === NOTIFICATION_TYPE.LEAVE_APPROVAL
                        ? 'common.notificationDestination.approval'
                        : 'common.notificationDestination.result'
                    )}
                  </NotificationDestination>
                )}
                <p>
                  {item.pisEmployeeName} <strong>{item.description}</strong>
                </p>
                <p>{item.subDescription}</p>
                <p>
                  Create Date :{' '}
                  {dayjs(item.createdAt).format('DD MMM YYYY HH:mm:ss')}
                </p>

                {!item.isRead && <i />}
              </ItemMetaContent>
            }
          />
        </NotificationItem>
      ))
    },
    [handleClickNoti, t]
  )

  const renderList = React.useMemo(() => {
    return (
      <WrapperComponent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h3>Notification</h3>
          {paginationRef.current.unreadCount > 0 && (
            <span
              style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }}
              onClick={handleMarkAll}
            >
              Read All
            </span>
          )}
        </div>

        <div id="scrollableDiv">
          <InfiniteScroll
            dataLength={
              paginationRef.current.currentPage * paginationRef.current.perPage
            }
            next={handleScroll}
            pullDownToRefreshThreshold={80}
            hasMore={
              // current list Noti < total Noti
              flatMap(map(notiList, 'list'))?.length <
              paginationRef.current.total
            }
            height={200}
            loader={
              pending && <Skeleton avatar paragraph={{ rows: 1 }} active />
            }
            endMessage={
              <EndNotification>
                <b>You have seen all notification</b>
              </EndNotification>
            }
            scrollableTarget="scrollableDiv"
          >
            <NotificationListWrapper
              dataSource={notiList}
              renderItem={renderNotification}
            />
          </InfiniteScroll>
        </div>
      </WrapperComponent>
    )
  }, [handleScroll, notiList, pending, renderNotification, handleMarkAll])

  return (
    <StyledDropdown
      ref={ref}
      overlay={renderList}
      trigger={trigger}
      {...props}
      open={open}
      onOpenChange={setOpen}
    >
      <Icon
        badge={{ content: paginationRef.current.unreadCount }}
        icon="Bell"
        color="#fff"
        size={30}
      />
    </StyledDropdown>
  )
}

export default React.forwardRef(NotificationPopup)
